import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { resources } from "@/i18n/resources";

export const LANGUAGE_STORAGE_KEY = "ediPortalLanguage";
export const LANGUAGE_SESSION_OVERRIDE_KEY = "ediPortalLanguageSessionOverride";
export const SUPPORTED_LANGUAGES = ["ko", "en", "zh-tw", "zh-cn"];

const normalizeLanguage = (value) =>
  SUPPORTED_LANGUAGES.includes(value?.toLowerCase()) ? value.toLowerCase() : "ko";

const getInitialLanguage = () =>
  normalizeLanguage(
    sessionStorage.getItem(LANGUAGE_STORAGE_KEY) ||
      navigator.language?.split("-")[0]
  );

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    sessionStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    setLanguageState(normalized);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback(
    (key, fallbackOrParams, params) => {
      const fallback =
        typeof fallbackOrParams === "string" ? fallbackOrParams : undefined;
      const replacements =
        fallbackOrParams && typeof fallbackOrParams === "object"
          ? fallbackOrParams
          : params;
      const value = key
        .split(".")
        .reduce((current, part) => current?.[part], resources[language]);
      const resolved =
        typeof value === "string"
          ? value
          : key
              .split(".")
              .reduce((current, part) => current?.[part], resources.ko);

      const text = typeof resolved === "string" ? resolved : fallback ?? key;
      return Object.entries(replacements || {}).reduce(
        (result, [name, replacement]) =>
          result.replaceAll(`{{${name}}}`, String(replacement)),
        text
      );
    },
    [language]
  );

  const translateText = useCallback(
    (text) => {
      if (typeof text !== "string" || language === "ko") return text;
      const trimmed = text.trim();
      return resources[language].raw?.[trimmed] || text;
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, translateText }),
    [language, setLanguage, t, translateText]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used within an I18nProvider");
  return context;
}
